// plugins/the-weather-company/layer-type-services/web-tile/twc-web-tile.layer.service.ts

import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { WEB_TILE_ROOT_URL } from '../../data-protocol-services/twc-web-tile.data.service';
import {
  TwcResponseData,
  TwcUpdateData
} from '../../data-protocol-services/twc.data.types';
import { TwcTimeValidationService } from '../../twc-time-validation.service';
import { initialTime } from '../../twc.mixin';
import { TwcLayerServiceInterface } from '../../twc.refresh-layer-plugin.service';
import { TwcWebTileLayer } from './twc-web-tile.layer';
import { TWC_CONFIG_TOKEN } from '../../plugins/twc-config.token';
import {
  createInitialLayerParams,
  getProductIdForFlightLevel
} from '../../twc-utils/layer-creation.utils';
import {
  normalizeSelectedFlightLevel,
  normalizeSelectedTime
} from '../../twc.mixin';
import { paramsObjectToArcgisUrlTemplate } from '../../twc-utils/url.utils';

@Injectable({
  providedIn: 'root'
})
export class TwcWebTileLayerService
  implements TwcLayerServiceInterface<TwcWebTileLayer>
{
  private readonly _env = inject(TWC_CONFIG_TOKEN);
  private readonly _twcApiKey = this._env.apiKey;
  private readonly _timeValidationService = inject(TwcTimeValidationService);

  createLayer$<TwcWebTileLayer>({
    twcProduct,
    tsInSeconds,
    timeMap,
    flightLevelValues
  }: TwcResponseData): Observable<TwcWebTileLayer> {
    const time = initialTime(timeMap);

    // Create base layer parameters
    const layerParams = createInitialLayerParams(
      twcProduct,
      timeMap,
      flightLevelValues
    );

    // Add URL template
    const urlTemplate = this._createTemplateUrl(
      twcProduct.defaultProductId,
      time,
      tsInSeconds
    );

    const layer = new TwcWebTileLayer({
      ...layerParams,
      urlTemplate
    });

    return of(layer as TwcWebTileLayer);
  }

  updateLayer(layer: TwcWebTileLayer, updateData: TwcUpdateData) {
    const selectedFlightLevel = normalizeSelectedFlightLevel(
      updateData.flightLevelValues,
      updateData.selectedFlightLevel,
      layer.selectedFlightLevel
    );
    const selectedTime = normalizeSelectedTime(
      updateData.timeMap,
      updateData.selectedTime,
      layer.selectedTime
    );

    // Update standard properties
    layer.updateFlightLevels(updateData.flightLevelValues, selectedFlightLevel);
    layer.updateTimeMap(updateData.timeMap, selectedTime);

    // Get product ID
    const productId = getProductIdForFlightLevel(
      updateData.twcProduct,
      selectedFlightLevel
    );

    // Update URL with validated parameters
    layer.urlTemplate = this._createTemplateUrl(
      productId,
      selectedTime,
      updateData.tsInSeconds
    );

    layer.refresh();
    return layer;
  }

  private _createTemplateUrl(
    productId: string,
    fts?: string,
    ts?: string
  ): string {
    const baseUrl = `${WEB_TILE_ROOT_URL}/tile/${productId}`;

    // Use validated parameters only
    const params: Record<string, string> = {
      apiKey: this._twcApiKey ?? 'api key not set',
      xyz: '{col}:{row}:{level}'
    };

    // Only add valid parameters
    if (ts) {
      params['ts'] = ts;

      if (fts) {
        const validFts = this._timeValidationService.getValidForecastTime(
          ts,
          fts
        );
        if (validFts) {
          params['fts'] = validFts;
        }
      }
    } else if (fts) {
      params['fts'] = fts;
    }

    return `${baseUrl}?${paramsObjectToArcgisUrlTemplate(params)}`;
  }
}
